import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { WkoLoadingSpinner } from "../WkoLoadingSpinner";
import { notifyError } from "../../utils/notifications";
import { BenchmarkCard } from "./BenchmarkCard";
const ACTIVITY_TYPE_COLOURS = {
    routine: "#E83052",
    overriding: "#5E90C3",
};
export function IndustryActivitiesPanel({ options, selectedActivityType, title = "Activities causing non-compliance", }) {
    const [loading, setLoading] = useState(true);
    const [fleetActivities, setFleetActivities] = useState([]);
    const [fleetActivityTypes, setFleetActivityTypes] = useState([]);
    const [industryActivities, setIndustryActivities] = useState([]);
    const [industryActivityTypes, setIndustryActivityTypes] = useState([]);
    const [hoveredActivity, setHoveredActivity] = useState(null);
    const fleetChartRef = useRef(null);
    const industryChartRef = useRef(null);
    const enableClick = selectedActivityType !== undefined;
    const activityTypeRef = useRef(options.activityType);
    activityTypeRef.current = options.activityType;
    useEffect(() => {
        if (!options.isReady)
            return;
        setFleetActivities([]);
        setFleetActivityTypes([]);
        setIndustryActivities([]);
        setIndustryActivityTypes([]);
        setLoading(true);
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            vesselTypes: options.vesselTypes.join(","),
            fleets: options.fleet.map((f) => f.ID).join(","),
            locationId: options.locationId == null ? "" : String(options.locationId),
        });
        fetch(`/api/benchmark/activitiesstat?${params}`)
            .then((res) => {
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((stats) => {
            const fleetTotal = stats.StatsByActivity.reduce((sum, x) => sum + x.UserFleetValue, 0);
            const industryTotal = stats.StatsByActivity.reduce((sum, x) => sum + x.IndustryValue, 0);
            setFleetActivities(stats.StatsByActivity.map((s) => ({
                name: s.Name,
                y: parseFloat(((s.UserFleetValue / fleetTotal) * 100).toFixed(2)),
                sliced: s.Name === options.activityType,
            })));
            setIndustryActivities(stats.StatsByActivity.map((s) => ({
                name: s.Name,
                y: parseFloat(((s.IndustryValue / industryTotal) * 100).toFixed(2)),
                sliced: s.Name === options.activityType,
            })));
            const fleetTypesTotal = stats.UserFleetRoutineTotal + stats.UserFleetOverridingTotal;
            setFleetActivityTypes([
                { name: "Routine operations", y: parseFloat(((stats.UserFleetRoutineTotal / fleetTypesTotal) * 100).toFixed(2)), color: ACTIVITY_TYPE_COLOURS.routine },
                { name: "Overriding operations", y: parseFloat(((stats.UserFleetOverridingTotal / fleetTypesTotal) * 100).toFixed(2)), color: ACTIVITY_TYPE_COLOURS.overriding },
            ]);
            const industryTypesTotal = stats.IndustryRoutineTotal + stats.IndustryOverridingTotal;
            setIndustryActivityTypes([
                { name: "Routine operations", y: parseFloat(((stats.IndustryRoutineTotal / industryTypesTotal) * 100).toFixed(2)), color: ACTIVITY_TYPE_COLOURS.routine },
                { name: "Overriding operations", y: parseFloat(((stats.IndustryOverridingTotal / industryTypesTotal) * 100).toFixed(2)), color: ACTIVITY_TYPE_COLOURS.overriding },
            ]);
        })
            .catch(() => {
            notifyError("An error has occurred when loading activity stats. Please try again.");
        })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options.isReady,
        options.startDate,
        options.endDate,
        options.vesselTypes.join(","),
        options.fleet.map((f) => f.ID).join(","),
        options.locationId,
        options.activityType,
    ]);
    const handleClick = (name) => {
        if (enableClick)
            selectedActivityType(name);
    };
    const chartOptions = useMemo(() => ({
        credits: { enabled: false },
        chart: { type: "pie", styledMode: false, backgroundColor: "transparent" },
        exporting: { enabled: false },
        title: { text: undefined },
        plotOptions: {
            pie: {
                allowPointSelect: enableClick,
                cursor: enableClick ? "pointer" : "default",
                states: { inactive: { opacity: 0.2 } },
                dataLabels: {
                    distance: -50,
                    enabled: true,
                    formatter: function () {
                        if (this.point.options.sliced) {
                            return `<b>${this.point.name}</b><br/><b>${this.point.y}%</b>`;
                        }
                        return undefined;
                    },
                },
                point: {
                    events: {
                        click: function () {
                            if (this.series.name === "Activities")
                                handleClick(this.name);
                        },
                        mouseOver: function () {
                            setHoveredActivity(this.name);
                        },
                        mouseOut: function () {
                            setHoveredActivity(null);
                        },
                    },
                },
            },
        },
        tooltip: { pointFormat: "<b>{point.y}%</b>" },
    }), [enableClick]);
    const fleetChartOptions = useMemo(() => ({
        ...chartOptions,
        series: [
            { type: "pie", colorByPoint: true, data: fleetActivities, name: "Activities", size: "100%", innerSize: "60%" },
            { type: "pie", colorByPoint: false, color: "gray", data: fleetActivityTypes, name: "Activity Types", size: "60%", innerSize: "80%", allowPointSelect: false },
        ],
    }), [chartOptions, fleetActivities, fleetActivityTypes]);
    const industryChartOptions = useMemo(() => ({
        ...chartOptions,
        series: [
            { type: "pie", colorByPoint: true, data: industryActivities, name: "Activities", size: "100%", innerSize: "60%" },
            { type: "pie", colorByPoint: false, color: "gray", data: industryActivityTypes, name: "Activity Types", size: "60%", innerSize: "80%", allowPointSelect: false },
        ],
    }), [chartOptions, industryActivities, industryActivityTypes]);
    useEffect(() => {
        if (!hoveredActivity)
            return;
        [fleetChartRef, industryChartRef].forEach((ref) => {
            const chart = ref.current?.chart;
            if (!chart)
                return;
            let point = chart.series[0]?.points?.find((p) => p.name === hoveredActivity);
            if (point) {
                if (enableClick && point.name !== activityTypeRef.current)
                    point.select(true, false);
            }
            else {
                point = chart.series[1]?.points?.find((p) => p.name === hoveredActivity);
            }
            if (point && point.name !== activityTypeRef.current)
                chart.tooltip.refresh(point);
        });
        return () => {
            [fleetChartRef, industryChartRef].forEach((ref) => {
                const chart = ref.current?.chart;
                if (!chart)
                    return;
                chart.series[0]?.points?.forEach((p) => {
                    if (enableClick && p.name !== activityTypeRef.current)
                        p.select(false, false);
                });
                chart.tooltip.hide();
            });
        };
    }, [hoveredActivity, enableClick]);
    if (loading) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx(WkoLoadingSpinner, {}) }));
    }
    if (fleetActivities.length === 0 && industryActivities.length === 0) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx("div", { className: "py-5", children: _jsx("p", { className: "text-secondary text-center mb-0", children: "No data for this selection." }) }) }));
    }
    return (_jsx(BenchmarkCard, { title: title, children: _jsxs("div", { className: "w-100", children: [_jsxs("div", { className: "w-100 activity-stats d-flex flex-row justify-content-center", children: [_jsxs("div", { className: "w-50", children: [_jsx("div", { className: "mb-1 text-center", children: "Data from your fleet" }), _jsx("div", { className: "soft-light-overlay", children: _jsx(HighchartsReact, { highcharts: Highcharts, options: fleetChartOptions, ref: fleetChartRef }) })] }), enableClick && (_jsx("div", { style: { zIndex: 1 }, className: "position-absolute alert alert-info col-2", role: "alert", children: "Click on an activity to see detailed stats" })), _jsxs("div", { className: "w-50", children: [_jsx("div", { className: "mb-1 text-center", children: "Industry average" }), _jsx("div", { className: "soft-light-overlay", children: _jsx(HighchartsReact, { highcharts: Highcharts, options: industryChartOptions, ref: industryChartRef }) })] })] }), _jsxs("div", { className: "w-100 activity-stats", children: [_jsx("div", { className: "mb-3", children: fleetActivities.map((a, i) => (_jsxs("div", { className: "me-4 mb-2 d-inline-block", style: { cursor: enableClick ? "pointer" : "default" }, onClick: () => handleClick(a.name), onMouseEnter: () => setHoveredActivity(a.name), onMouseLeave: () => setHoveredActivity(null), children: [_jsx("div", { className: `symbol d-inline-block align-middle highcharts-color-bg${i}`, style: { height: "1.5rem", width: "1.5rem" }, children: "\u00A0" }), _jsx("span", { className: "ps-2", children: a.name })] }, a.name))) }), _jsx("div", { children: fleetActivityTypes.map((a) => (_jsxs("div", { className: "me-4 mb-2 d-inline-block", children: [_jsx("span", { className: "symbol d-inline-block align-middle", style: { height: "0.5rem", width: "5rem", backgroundColor: a.color } }), _jsx("span", { className: "ps-2", children: a.name })] }, a.name))) })] })] }) }));
}
